import * as Sentry from '@sentry/react';
import React, { useState } from 'react';
import { Box, Button, Container, Typography, Tooltip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Helmet } from 'react-helmet-async';
import { useTheme } from '@mui/material/styles';

function CopyButton({ textToCopy }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  return (
    <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
      <Button
        variant="outlined"
        color="primary2"
        onClick={handleCopy}
        startIcon={copied ? <CheckIcon color="success" /> : <ContentCopyIcon />}
      >
        {copied ? 'Copiado' : 'Copiar error'}
      </Button>
    </Tooltip>
  );
}

function ErrorFallback({ error, errorInfo }) {
  const theme = useTheme();
  const mobileDevice = useMediaQuery(theme.breakpoints.down('xl'));

  return (
    <>
      <Helmet>
        <title>Error | Fintecsol Factoring</title>
      </Helmet>
      <Box
        sx={{
          alignItems: 'center',
          backgroundColor: 'background.paper',
          display: 'flex',
          minHeight: '100%',
          px: 3,
          py: '80px',
        }}
      >
        <Container maxWidth="lg">
          <Typography align="center" color="textPrimary" variant={mobileDevice ? 'h4' : 'h1'}>
            ¡Algo salió mal!
          </Typography>
          <Typography align="center" color="textSecondary" sx={{ mt: 2 }} variant="subtitle1">
            Recargue la página e intente nuevamente. En caso de que el error persista, por favor comuníquese con el
            administrador.
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
            <Box
              alt="Under development"
              component="img"
              src={`/static/error/error401_${theme.palette.mode}.svg`}
              sx={{ height: 'auto', maxWidth: '100%', width: 400 }}
            />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2, mt: 6, px: -3, justifyContent: 'center', width: '100%' }}>
            <CopyButton textToCopy={`${error?.toString()}\n${errorInfo?.componentStack ?? ''}`} />
            <Button
              variant="contained"
              onClick={() => {
                sessionStorage.clear();
                window.location.reload();
              }}
            >
              Recargar página
            </Button>
          </Box>
        </Container>
      </Box>
    </>
  );
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({ errorInfo });
    Sentry.captureException(error, { contexts: { react: { componentStack: errorInfo.componentStack } } });
  }

  render() {
    const { hasError, error, errorInfo } = this.state;
    const { children } = this.props;

    if (hasError) {
      return <ErrorFallback error={error} errorInfo={errorInfo} />;
    }

    return children;
  }
}

export default ErrorBoundary;
